import React, { useState, useEffect } from 'react';
import {
    X, Bell, AlertTriangle, Pin, ExternalLink,
    Paperclip, Download, ChevronLeft, ChevronRight,
    Eye
} from 'lucide-react';
import api from '../../utils/api';

const NoticePopupModal = ({ isOpen, onClose }) => {
    const [notices, setNotices] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            fetchPopupNotices();
        }
    }, [isOpen]);

    const fetchPopupNotices = async () => {
        setLoading(true);
        try {
            const response = await api.get('/notices/employee/popup-notices');
            setNotices(response.data.notices || []);
            setCurrentIndex(0);
        } catch (error) {
            console.error('Error fetching popup notices:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = async () => {
        if (notices.length === 0) return;

        const currentNotice = notices[currentIndex];

        try {
            await api.post(`/notices/employee/${currentNotice.id}/dismiss-popup`);

            // Remove from local state
            const newNotices = notices.filter((_, i) => i !== currentIndex);
            setNotices(newNotices);

            // Adjust current index if needed
            if (currentIndex >= newNotices.length && newNotices.length > 0) {
                setCurrentIndex(newNotices.length - 1);
            }

            // Close if no more notices
            if (newNotices.length === 0) {
                onClose();
            }
        } catch (error) {
            console.error('Error dismissing notice:', error);
        }
    };

    const handleDismissAll = async () => {
        try {
            // Dismiss all notices
            await Promise.all(
                notices.map(notice =>
                    api.post(`/notices/employee/${notice.id}/dismiss-popup`)
                )
            );
            setNotices([]);
            onClose();
        } catch (error) {
            console.error('Error dismissing all notices:', error);
        }
    };

    const handleNext = () => {
        if (currentIndex < notices.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgent':
                return <AlertTriangle className="w-6 h-6 text-red-500" />;
            case 'pinned':
                return <Pin className="w-6 h-6 text-amber-500" />;
            default:
                return <Bell className="w-6 h-6 text-primary" />;
        }
    };

    const getPriorityGradient = (priority) => {
        switch (priority) {
            case 'urgent':
                return 'from-red-600 to-red-700';
            case 'pinned':
                return 'from-amber-500 to-amber-600';
            default:
                return 'from-primary to-primary-hover';
        }
    };

    if (!isOpen || loading) return null;
    if (notices.length === 0) return null;

    const currentNotice = notices[currentIndex];

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-0 md:p-4 transition-all">
            <div className="bg-white w-full h-full md:h-auto md:max-h-[85vh] md:max-w-lg md:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className={`px-4 py-3 md:px-6 md:py-4 safe-area-top bg-gradient-to-r ${getPriorityGradient(currentNotice.priority)}`}>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-xl">
                                {getPriorityIcon(currentNotice.priority)}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-white/80 text-sm font-medium">
                                        Important Notice
                                    </span>
                                    {currentNotice.priority === 'urgent' && (
                                        <span className="px-2 py-0.5 bg-white/20 text-white text-xs rounded-full font-medium">
                                            Urgent
                                        </span>
                                    )}
                                </div>
                                {notices.length > 1 && (
                                    <p className="text-white/60 text-xs mt-1">
                                        {currentIndex + 1} of {notices.length} notices
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={handleDismiss}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            title="Dismiss this notice"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                        {currentNotice.title}
                    </h2>

                    <div
                        className="prose prose-sm max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{ __html: currentNotice.content }}
                    />

                    {/* External Link */}
                    {currentNotice.external_link && (
                        <div className="mt-6">
                            <a
                                href={currentNotice.external_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                <span className="font-medium">Open Link</span>
                            </a>
                        </div>
                    )}

                    {/* Attachments */}
                    {currentNotice.attachments && currentNotice.attachments.length > 0 && (
                        <div className="mt-6">
                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                                <Paperclip className="w-4 h-4" />
                                Attachments ({currentNotice.attachments.length})
                            </h4>
                            <div className="space-y-2">
                                {currentNotice.attachments.map((file, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                            <span className="text-sm font-medium text-gray-700 truncate">
                                                {file.file_name}
                                            </span>
                                        </div>
                                        <a
                                            href={file.file_url}
                                            download={file.file_name}
                                            className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                            title="Download"
                                        >
                                            <Download className="w-4 h-4" />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer with Navigation */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-t border-gray-200 bg-gray-50 safe-area-bottom">
                    {notices.length > 1 ? (
                        <div className="flex items-center justify-between">
                            <button
                                onClick={handlePrev}
                                disabled={currentIndex === 0}
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-colors ${currentIndex === 0
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>

                            <div className="flex items-center gap-1">
                                {notices.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentIndex(i)}
                                        className={`w-2 h-2 rounded-full transition-colors ${i === currentIndex ? 'bg-primary' : 'bg-gray-300'
                                            }`}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={handleNext}
                                disabled={currentIndex === notices.length - 1}
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-colors ${currentIndex === notices.length - 1
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleDismiss}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors"
                        >
                            <Eye className="w-4 h-4" />
                            Got it
                        </button>
                    )}

                    {notices.length > 1 && (
                        <button
                            onClick={handleDismissAll}
                            className="w-full mt-3 px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                        >
                            Dismiss all notices
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NoticePopupModal;

